import { useState } from "react";
import { WorldMap } from "@/components/game/WorldMap";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Home, Check, Loader2 } from "lucide-react";

/**
 * First-run country picker.
 *
 * Shown as a fullscreen modal the very first time a player signs in. Blocks
 * the game UI until a nation is chosen — the choice is permanent (enforced
 * server-side) so a bit of ceremony is appropriate.
 *
 * Reuses ``WorldMap`` in a lightweight "pick" mode: the player can search,
 * click any country to preview it, and confirm at the bottom. Region tint
 * is disabled here so the world reads clearly as a homeland roster rather
 * than a claim board.
 */
export function HomeCountryPicker({ onPicked }) {
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  async function confirm() {
    if (!selected || busy) return;
    setBusy(true);
    try {
      await api.post("/api/game/map/home-country", { iso_a3: selected.iso_a3 });
      toast.success(`Welcome home, ${selected.name}.`);
      onPicked?.(selected);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#050914]/95 backdrop-blur-sm flex flex-col"
      data-testid="home-country-picker"
    >
      <header className="p-6 lg:p-8 text-center max-w-3xl mx-auto">
        <div className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 flex items-center justify-center gap-2">
          <Home size={11} /> Chapter I · The Homeland
        </div>
        <h2 className="font-display text-3xl lg:text-4xl heading-gilded mt-2">
          Where do you call home?
        </h2>
        <p className="text-stone-400 font-body mt-2 leading-relaxed">
          Choose the nation you will build your legacy in. This is where your
          name will be written into the roll of Territoria — the choice is
          permanent and shapes your first steps in the world.
        </p>
      </header>

      <div className="flex-1 min-h-0 px-4 lg:px-8 pb-4">
        <WorldMap
          selectedIso={selected?.iso_a3}
          onSelectCountry={setSelected}
          markers={[]}
        />
      </div>

      <footer className="p-4 lg:p-6 border-t border-stone-800/70 bg-[#0b0906]">
        <div className="max-w-4xl mx-auto flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            {selected ? (
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded shrink-0" style={{ background: selected.color, boxShadow: `0 0 20px ${selected.color}80` }} />
                <div className="min-w-0">
                  <div className="uppercase tracking-widest text-[10px] font-numeric text-amber-500">
                    Selected homeland · {selected.iso_a3}
                  </div>
                  <div className="font-display text-lg heading-gilded truncate">
                    {selected.name}
                  </div>
                  {selected.legacy_name && (
                    <div className="text-[11px] font-body italic text-stone-500 truncate">
                      formerly {selected.legacy_name}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-stone-500 font-body italic">
                Click any country on the map to preview it.
              </div>
            )}
          </div>
          <button
            disabled={!selected || busy}
            onClick={confirm}
            className="px-5 py-2.5 rounded-md border border-amber-600 bg-amber-900/25 text-amber-200 font-numeric uppercase tracking-widest text-sm hover:bg-amber-900/40 hover:border-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            data-testid="home-country-confirm"
          >
            {busy
              ? <><Loader2 size={14} className="animate-spin" /> setting…</>
              : <><Check size={14} /> Confirm homeland</>
            }
          </button>
        </div>
      </footer>
    </div>
  );
}
